import React from 'react';
import { Card } from './ui/card';
import { MapPin, Phone, Mail, MessageCircle } from 'lucide-react';

export const LocationsMap = ({ locations, openingHours }) => {
  return (
    <section id="kontakt" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Finden Sie uns
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Besuchen Sie uns an einem unserer Standorte in Magdeburg
          </p>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 mb-12">
          {locations.map((location, index) => (
            <Card
              key={location.id}
              className="relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 border-0 flex flex-col"
              data-testid={`location-card-${location.id}`}
            >
              {/* Neu-eröffnet Ribbon */}
              {location.newlyOpened && (
                <div className="absolute top-4 right-[-40px] rotate-45 z-10 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-[11px] font-bold uppercase tracking-wider px-12 py-1 shadow-lg">
                  Neu eröffnet
                </div>
              )}
              {/* Coming Soon Overlay Ribbon */}
              {location.comingSoon && (
                <div className="absolute top-4 right-[-40px] rotate-45 z-10 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-[11px] font-bold uppercase tracking-wider px-12 py-1 shadow-lg">
                  Bald da
                </div>
              )}

              {/* Brand Logo */}
              <div className="bg-white p-6 flex justify-center border-b h-[112px] items-center">
                <img
                  src={location.logo}
                  alt={`${location.brand} logo`}
                  className={location.brand === 'Omoda & Jaecoo' ? 'h-8 w-auto max-w-[200px] object-contain' : 'h-16 w-auto object-contain'}
                />
              </div>

              {/* Map */}
              <div className="relative h-48 overflow-hidden">
                <iframe
                  src={location.mapUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`${location.brand} Standort ${location.address.street}`}
                ></iframe>
              </div>

              {/* Content */}
              <div className="p-6 bg-white flex-1 flex flex-col">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Autohaus Wilke - {location.brand}
                </h3>
                {location.comingSoonNote ? (
                  <div className="inline-flex items-center gap-1.5 mb-4 px-3 py-1 bg-amber-50 border border-amber-200 rounded-full text-amber-800 text-xs font-medium self-start" data-testid={`location-coming-soon-${location.id}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                    {location.comingSoonNote}
                  </div>
                ) : location.infoNote ? (
                  <div className="inline-flex items-center gap-1.5 mb-4 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-emerald-800 text-xs font-medium self-start" data-testid={`location-info-${location.id}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    {location.infoNote}
                  </div>
                ) : location.additionalBrandsNote ? (
                  <div className="inline-flex items-center gap-1.5 mb-4 px-3 py-1 bg-amber-50 border border-amber-200 rounded-full text-amber-800 text-xs font-medium self-start" data-testid={`location-note-${location.id}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                    {location.additionalBrandsNote}
                  </div>
                ) : (
                  <div className="mb-4"></div>
                )}
                
                <div className="space-y-3 mb-6 flex-1">
                  {/* Address */}
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-red-50 to-red-100 rounded-lg flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-gray-900 font-semibold text-sm">
                        {location.address.street}
                      </p>
                      <p className="text-gray-600 text-sm">
                        {location.address.zip} {location.address.city}
                      </p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-red-50 to-red-100 rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5 text-red-600" />
                    </div>
                    <a
                      href={`tel:${location.phone.replace(/\s/g, '')}`}
                      className="text-gray-900 font-semibold text-sm hover:text-red-600 transition-colors"
                    >
                      {location.phone}
                    </a>
                  </div>

                  {/* Email */}
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-red-50 to-red-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-red-600" />
                    </div>
                    <a
                      href={`mailto:${location.email}`}
                      className="text-gray-900 font-semibold text-sm hover:text-red-600 transition-colors break-all"
                    >
                      {location.email}
                    </a>
                  </div>

                  {/* WhatsApp */}
                  {location.whatsapp && (
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-green-50 to-green-100 rounded-lg flex items-center justify-center">
                        <MessageCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <a
                        href={`https://wa.me/${location.whatsapp.replace(/[^0-9]/g, '')}?text=Hallo,%20ich%20interessiere%20mich%20für%20ein%20Fahrzeug%20am%20Standort%20${encodeURIComponent(location.address.street)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-900 font-semibold text-sm hover:text-green-600 transition-colors"
                        data-testid={`location-whatsapp-${location.id}`}
                      >
                        {location.whatsappDisplay || location.whatsapp}
                      </a>
                    </div>
                  )}
                </div>

                {/* External Website Button (only for OJ Standort) */}
                {location.brand === 'Omoda & Jaecoo' && (
                  <a
                    href={location.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-auto inline-flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold text-sm rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
                    data-testid={`location-website-${location.id}`}
                  >
                    Zur Omoda &amp; Jaecoo Website
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </a>
                )}

                {/* Accent Border */}
                <div
                  className="absolute top-0 left-0 right-0 h-1"
                  style={{ backgroundColor: location.color }}
                ></div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
